// The browser's visitor id: one random uuid per browser, for a visitor who allowed analytics (the site's consent rule,
// consent-policy.js), so their visits count as one visitor's and can be tied to the account that signs in on it
// (promptchan-backend growth.visits.visitor_id and growth.visitor_accounts; docs/reference/visit-tracking.md there).
//
// The cookie holds `<uuid>.<when it was made, in Unix seconds>` and lives 13 months from then, the usual limit for an
// analytics cookie; a later visit never extends it. The middleware gives it out, and on every page load sets it again
// with the time it has left: that turns a cookie the page made itself (when a visitor accepts the banner mid-visit) into
// one the server set, which Safari does not cap at 7 days. No browser dependencies: the middleware imports it.
import { isKnownLandingBot, isLandingPrefetch } from './landing-experiment.js'

export const VISITOR_COOKIE = 'pc_vid'
export const VISITOR_MAX_AGE = 60 * 60 * 24 * 395
const UUID =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/

/** { id, at, left } from the cookie's value (left: seconds it has to live), or null when it is not one or has run out. */
export function parseVisitorCookie(raw, now = Date.now()) {
  if (typeof raw !== 'string') return null
  const [id, made, extra] = raw.split('.')
  const at = Number(made)
  const seconds = Math.floor(now / 1000)
  if (
    extra !== undefined ||
    !UUID.test(id || '') ||
    !Number.isInteger(at) ||
    at > seconds + 300
  )
    return null
  const left = at + VISITOR_MAX_AGE - seconds
  return left > 0 ? { id, at, left } : null
}

export function newVisitor(now = Date.now()) {
  return {
    id: crypto.randomUUID(),
    at: Math.floor(now / 1000),
    left: VISITOR_MAX_AGE,
  }
}

export function visitorCookieValue(visitor) {
  return `${visitor.id}.${visitor.at}`
}

/**
 * On a request the middleware answers: give a visitor who allowed analytics an id, or set theirs again on a page load
 * with the time it has left; take it from one who has not. Crawlers and prefetches get nothing.
 */
export function recordVisitor(req, res, allowed, now = Date.now()) {
  const raw = req.cookies.get(VISITOR_COOKIE)?.value
  if (!allowed) {
    if (raw !== undefined) res.cookies.delete(VISITOR_COOKIE)
    return
  }
  if (
    req.method !== 'GET' ||
    isLandingPrefetch(req.headers) ||
    isKnownLandingBot(req.headers)
  )
    return
  const current = parseVisitorCookie(raw, now)
  const dest = req.headers.get('sec-fetch-dest')
  const page = dest
    ? dest === 'document'
    : (req.headers.get('accept') || '').includes('text/html')
  if (current && !page) return
  const visitor = current || newVisitor(now)
  res.cookies.set(VISITOR_COOKIE, visitorCookieValue(visitor), {
    path: '/',
    sameSite: 'lax',
    secure: req.nextUrl.protocol === 'https:',
    httpOnly: false,
    maxAge: visitor.left,
  })
}
